/* eslint-disable react-hooks/exhaustive-deps */
import { Button } from '@efexteam/components';
import FileInputV3 from '@efex-components/EfexDS/FileInput/FileInput';
import clientSvc from '@efex-services/client/clientSvc';
import { OnboardingDocumentTypeIds } from '@efex-services/documents/types';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { Grid, Typography } from '@mui/material';
import { MxOnboardingRequest } from '@pages/onboarding/Colombia/types';
import CompanyOperation from '@pages/onboarding/components/CompanyOperation/CompanyOperation';
import {
  selectBusiness,
  selectProfileData,
} from '@pages/profile/store/selectors';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useApiCall } from 'use-api-call';
import { UploadFileExampleProps } from '@efex-components/EfexDS/FileInput/FileInput.type';
import FileInputExampleModal from '@efex-components/EfexDS/FileInputExampleModal';
import { FormProvider, useForm } from 'react-hook-form';
import useFileService from '@efex-components/EfexDS/FileInput/useFileService';
import { ENTITY_DOCUMENTS } from '../../constants/config';
import { useFormData } from '../../context';
import { IMexicoOnboardingPartials } from '../../types';

interface KycMxDocumentsProps {
  nextFormStep: () => void;
}

const KycMxDocuments = ({ nextFormStep }: KycMxDocumentsProps) => {
  const { t } = useTranslation();
  const [exampleValue, setExampleValue] =
    useState<UploadFileExampleProps | null>(null);
  const company = useSelector(selectBusiness);
  const userProfile = useSelector(selectProfileData);
  const {
    getOnboardingMx,
    data,
  }: {
    getOnboardingMx: () => void;
    data: IMexicoOnboardingPartials;
  } = useFormData();

  const useFileServiceMethods = useFileService();

  const {
    onboardingDocuments,
    handleChangeFile,
    handleDropChangeFile,
    handleDeleteFile,
  } = useFileServiceMethods;

  const [hasAllRequiredDocuments, setHasAllRequiredDocuments] = useState(false);

  const methods = useForm<{
    isCompanyOperating: boolean;
    hasForeignOperations: boolean;
  }>({
    defaultValues: {
      isCompanyOperating: !!data.isCompanyOperating,
      hasForeignOperations: !!data.hasForeignOperations,
    },
  });

  const {
    data: completeMxKyc,
    invoke: completKycMx,
    loading: loadingCompleteMxKyc,
  } = useApiCall(() =>
    clientSvc.onboardingMx(
      Number(company?.enterpriseId),
      {
        isCompanyOperating: methods.watch('isCompanyOperating'),
        hasForeignOperations: methods.watch('hasForeignOperations'),
      } as unknown as MxOnboardingRequest,
      'final'
    )
  );

  const handleConfirmMx = () => {
    completKycMx();
  };

  useEffect(() => {
    if (completeMxKyc) {
      nextFormStep();
    }
  }, [completeMxKyc]);

  useEffect(() => {
    const requiredDocuments =
      ENTITY_DOCUMENTS[userProfile?.userType || 'COMPANY'];

    if (onboardingDocuments && requiredDocuments) {
      const requiredDocumentTypeIds = new Set(
        requiredDocuments.map(
          (doc) =>
            OnboardingDocumentTypeIds[
              doc.name as keyof typeof OnboardingDocumentTypeIds
            ]
        )
      );

      requiredDocumentTypeIds.add(OnboardingDocumentTypeIds.companyOperation);

      const isCompanyOperating = methods.watch('isCompanyOperating');
      const hasForeignOperations = methods.watch('hasForeignOperations');

      if (isCompanyOperating && hasForeignOperations) {
        requiredDocumentTypeIds.add(OnboardingDocumentTypeIds.businessNature);
      }

      const uploadedDocumentTypeIds = new Set(
        onboardingDocuments.map((doc) => doc?.document?.type?.id)
      );

      const allRequiredDocumentsUploaded = Array.from(
        requiredDocumentTypeIds
      ).every((typeId) => uploadedDocumentTypeIds.has(typeId));

      setHasAllRequiredDocuments(allRequiredDocumentsUploaded);
    } else {
      setHasAllRequiredDocuments(false);
    }
  }, [
    onboardingDocuments,
    userProfile?.userType,
    methods.watch('isCompanyOperating'),
    methods.watch('hasForeignOperations'),
  ]);

  useEffect(() => {
    methods.setValue('isCompanyOperating', !!data.isCompanyOperating);
    methods.setValue('hasForeignOperations', !!data.hasForeignOperations);
  }, [data]);

  useEffect(() => {
    getOnboardingMx();
  }, []);

  return (
    <FormProvider {...methods}>
      <Typography variant="h4" className="!font-medium">
        {t('onbCompanyDocumentationMex')}
      </Typography>
      <Typography className="!leading-5 !mt-[10px] !mb-[20px]">
        {t('onbCompanyDocumentationMexTitle')}
      </Typography>
      <Grid container xs={12} className="!mb-[15px]">
        {ENTITY_DOCUMENTS[userProfile?.userType || 'COMPANY'].map(
          (entityDocument) => (
            <Grid item xs={12} key={entityDocument.name}>
              <FileInputV3
                key={entityDocument.name}
                documents={
                  onboardingDocuments && onboardingDocuments.length > 0
                    ? onboardingDocuments?.filter(
                        (element) =>
                          element?.document?.type?.id ===
                          OnboardingDocumentTypeIds[
                            entityDocument.name as keyof typeof OnboardingDocumentTypeIds
                          ]
                      )
                    : []
                }
                onChange={handleChangeFile}
                onDropChange={handleDropChangeFile}
                onDelete={handleDeleteFile}
                inputName={entityDocument.name}
                isSingleFile
                title={
                  t(
                    entityDocument.label || entityDocument.translation
                  ) as string
                }
                wrapperClasses="max-w-[500px]"
                example={entityDocument.example}
                onExampleClick={(values) => setExampleValue(values)}
              />
            </Grid>
          )
        )}
      </Grid>
      <Grid item xs={12}>
        <CompanyOperation useExistingFileService={useFileServiceMethods} />
      </Grid>
      <Grid item xs={12} className="flex justify-center gap-10  max-w-[1000px]">
        <Button
          appearance="primary"
          size="large"
          disabled={loadingCompleteMxKyc || !hasAllRequiredDocuments}
          endIcon={
            loadingCompleteMxKyc ? (
              <AutorenewIcon className="animate-spin" />
            ) : undefined
          }
          onClick={handleConfirmMx}
        >
          {t('continue')}
        </Button>
      </Grid>
      <FileInputExampleModal
        data={exampleValue}
        open={!!exampleValue}
        onClose={() => setExampleValue(null)}
      />
    </FormProvider>
  );
};

export default KycMxDocuments;
